import { initializeApp, getApps, getApp } from 'firebase/app';
import { 
  getAuth, 
  GoogleAuthProvider, 
  signInWithPopup, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut as firebaseSignOut, 
  onAuthStateChanged, 
  sendPasswordResetEmail, 
  sendEmailVerification, 
  User 
} from 'firebase/auth';
import { 
  getFirestore, 
  initializeFirestore, 
  persistentLocalCache,
  persistentMultipleTabManager,
  setLogLevel,
  doc, 
  getDoc, 
  getDocFromServer,
  setDoc, 
  onSnapshot 
} from 'firebase/firestore';
import firebaseConfig from '../../firebase-applet-config.json';

// Silence non-fatal Firestore network transition warnings (e.g., 10s backend connection watchdog when offline or on mobile cellular proxies)
setLogLevel('error');

const app = getApps().length > 0 ? getApp() : initializeApp({
  apiKey: firebaseConfig.apiKey,
  authDomain: firebaseConfig.authDomain,
  projectId: firebaseConfig.projectId,
  storageBucket: firebaseConfig.storageBucket,
  messagingSenderId: firebaseConfig.messagingSenderId,
  appId: firebaseConfig.appId,
});

export const auth = getAuth(app);

// Target database ID from configuration
const databaseId = firebaseConfig.firestoreDatabaseId && firebaseConfig.firestoreDatabaseId !== '(default)'
  ? firebaseConfig.firestoreDatabaseId
  : undefined;

let dbInstance;
try {
  let cacheConfig;
  try {
    if (typeof window !== 'undefined' && 'indexedDB' in window) {
      cacheConfig = persistentLocalCache({
        tabManager: persistentMultipleTabManager()
      });
    }
  } catch {
    // If IndexedDB is blocked or quota restricted, Firestore defaults to memory cache
  }

  dbInstance = initializeFirestore(app, {
    experimentalAutoDetectLongPolling: true,
    ignoreUndefinedProperties: true,
    ...(cacheConfig ? { localCache: cacheConfig } : {})
  }, databaseId);
} catch (e) {
  // If already initialized, fallback to getFirestore
  dbInstance = databaseId ? getFirestore(app, databaseId) : getFirestore(app);
}

export const db = dbInstance;

export const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: 'select_account' });

// Connection test helper with 4-second fail-fast timeout
export async function testFirestoreConnection(): Promise<boolean> {
  try {
    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('connection-timeout')), 4000)
    );
    await Promise.race([
      getDocFromServer(doc(db, 'system', 'connection_health')),
      timeoutPromise
    ]);
    return true;
  } catch {
    // Offline or sandboxed mode - local encrypted vault handles data seamlessly
    return false;
  }
}

export {
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  firebaseSignOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  sendEmailVerification
};
export type { User };
